import mergeClasses from '../../../utils/shallowMerge';
import Button from '../../atoms/button';
import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import Title from '../../typography/title';
import { ResponsiveImage } from '../../atoms/image/ResponsiveImage';

import defaultClasses from './MediaTextTeaser.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { StylesCustomizer } from '../../molecules/stylesCustomizer';
import VideoPlayer from '../../atoms/VideoPlayer';

import { useInView } from 'react-intersection-observer';

const MediaTextTeaser = ({
  background,
  image = null,
  imageMobileOrderReverse = false,
  label = '',
  labelStyles,
  layout = 'left',
  subTitle = '',
  subTitleStyles,
  title = '',
  titleStyles,
  verticalPosition = 'center', // start, center, end
  horizontalPosition, // left, center, right
  paragraphText = '',
  paragraphTextStyles,
  buttons = '',
  titleAlignment = 'left', // left center right
  paragraphSize = 'sm',
  boxSize = 'largeImageBox',
  titleTag = 'h2',
  textBoxMaxWidth = '400',
  textBoxWidth,
  showOverlay = false,
  overlayTheme = 'light',
  customClasses = null,
  showVideoControls,
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const isDarkTheme = showOverlay && overlayTheme === 'dark';
  const buttonShouldBeInverted = overlayTheme !== 'light' || !showOverlay;
  const { ref: mediaRef, inView } = useInView();

  const customLayout = clsx(styles.root, {
    [styles.left]: layout === 'textLeft',
    [styles.right]: layout === 'textRight',
    [styles.largeImageBox]: boxSize === 'largeImageBox',
    [styles.largeTextBox]: boxSize === 'largeTextBox',
  });

  const textAlignment = clsx(styles.textBlock, {
    [styles.textContentLeft]: titleAlignment === 'left',
    [styles.textContentCenter]: titleAlignment === 'center',
    [styles.textContentRight]: titleAlignment === 'right',
  });

  const buttonLinks =
    Array.isArray(buttons) &&
    !!buttons.length &&
    buttons
      ?.filter((button) => !!button?.buttonUrl?.target || !!button?.buttonUrl?.link)
      .map((it, index) => (
        <div key={index} className={clsx(styles.btnWrapper, styles[`type_${it.buttonType}`])}>
          <Button
            isOutlined={false}
            variant={it.buttonType}
            shape="square"
            as={it.buttonType === 'text' ? 'text' : 'link'}
            nodeLink={it?.buttonUrl}
            className={clsx(styles.button, styles[it.buttonType], {
              [styles.inverted]: buttonShouldBeInverted,
            })}
            isInverted={buttonShouldBeInverted}
            hiddenText={`Checkout it out on ${it?.buttonUrl?.target}`}
            ariaLabel={it.buttonTitle}
          >
            {it.buttonTitle}
          </Button>
        </div>
      ));

  let wrapperBackgroundColor = React.useMemo(() => {
    if (isDarkTheme) {
      return background ?? '#41414166';
    }
    return background || 'initial';
  }, [isDarkTheme, background]);

  const renderMedia = React.useMemo(
    () => (
      <div
        className={clsx(styles.image, {
          [styles.imageOrderReverse]: imageMobileOrderReverse,
        })}
      >
        {image.media.resourceType === 'video' ? (
          <VideoPlayer
            controls={showVideoControls}
            url={image.media.file}
            aspectRatio={image?.ratio?.replace(':', '/')}
          />
        ) : (
          <ResponsiveImage source={image} loading={inView ? 'eager' : 'lazy'} />
        )}
      </div>
    ),
    [image, inView],
  );

  const LabelText = (variables) => {
    return (
      <Text as="span" weight="semibold" uppercase className={clsx(textAlignment, styles.label)} variables={variables}>
        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </Text>
    );
  };
  const TitleText = (variables) => {
    return (
      <Heading className={clsx(textAlignment, styles.headingTitle)} as={titleTag} placement="plp" variables={variables}>
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };
  const TitleSubText = (variables) => {
    return (
      <Text
        className={clsx(textAlignment, styles.subtitle)}
        as="p"
        variables={variables}
        align={titleAlignment === 'left' ? 'start' : titleAlignment === 'right' ? 'end' : 'center'}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: subTitle,
          }}
        />
      </Text>
    );
  };

  const ParagraphText = (variables) => {
    return (
      <Text
        className={clsx(textAlignment, styles.paragraphText, styles[`paragraphText_${paragraphSize}`])}
        weight="light"
        align={titleAlignment === 'left' ? 'start' : titleAlignment === 'right' ? 'end' : 'center'}
        as="p"
        variables={variables}
      >
        <span className={styles.htmlWrap} dangerouslySetInnerHTML={{ __html: paragraphText }} />
      </Text>
    );
  };

  const StyledLabel = StylesCustomizer(LabelText);
  const StyledTitle = StylesCustomizer(TitleText);
  const StyledTitleSub = StylesCustomizer(TitleSubText);
  const StyledParagraphText = StylesCustomizer(ParagraphText);

  return (
    <div className={customLayout} ref={mediaRef}>
      {renderMedia}

      <div
        style={{
          backgroundColor: wrapperBackgroundColor,
          '--flex-align-items': verticalPosition,
          '--flex-justify-content':
            horizontalPosition === 'left' ? 'flex-start' : horizontalPosition === 'right' ? 'flex-end' : 'center',
          '--text-max-width': textBoxMaxWidth ? textBoxMaxWidth + 'px' : 'auto',
          '--text-width': textBoxWidth ? textBoxWidth + '%' : 'auto',
        }}
        className={styles.wrapper}
      >
        <Title className={textAlignment} customClasses={{ title: styles.title }}>
          {label && <StyledLabel styles={labelStyles} />}
          {title && <StyledTitle styles={titleStyles} />}
          {subTitle && <StyledTitleSub styles={subTitleStyles} />}
          {paragraphText && <StyledParagraphText styles={paragraphTextStyles} />}
          {buttons && <div className={`${styles.buttonGroup} ${textAlignment}`}>{buttonLinks}</div>}
        </Title>
      </div>
    </div>
  );
};

export default MediaTextTeaser;

MediaTextTeaser.propTypes = {
  verticalPosition: PropTypes.oneOf(['start', 'center', 'end']),
  layout: PropTypes.oneOf(['left', 'right', 'textLeft', 'textRight']),
  titleAlignment: PropTypes.oneOf(['left', 'center', 'right']),
  overlayTheme: PropTypes.oneOf(['light', 'dark']),
  showOverlay: PropTypes.bool,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  imageMobileOrderReverse: PropTypes.bool,
  label: PropTypes.string,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  paragraphText: PropTypes.string,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      variant: PropTypes.string,
      shape: PropTypes.string,
      isOutlined: PropTypes.bool,
      as: PropTypes.string,
      isInverted: PropTypes.bool,
      text: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
  customClasses: PropTypes.shape({
    root: PropTypes.string,
    image: PropTypes.string,
    wrapper: PropTypes.string,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    paragraphText: PropTypes.string,
    textBlock: PropTypes.string,
    textContentLeft: PropTypes.string,
    textContentCenter: PropTypes.string,
    textContentRight: PropTypes.string,
    buttonGroup: PropTypes.string,
    button: PropTypes.string,
  }),
  subTitleFontSize: PropTypes.number,
  subTitleLineHeight: PropTypes.number,
  subTitleMobileFontSize: PropTypes.number,
  subTitleMobileLineHeight: PropTypes.number,
};
